/*
 * Copyright (c) 2021-2024. StockAnywhere CC
 * StockAnywhere - https://www.stockanywhere.io
 * View license agreement and terms of use at https://www.stockanywhere.io/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-06-30
 */

export interface DynamicObject {
    [key: string]: string | number | boolean;
}

export interface IQueryListComms {
    query: string;
    schema: string;
}



export interface IQueryListModal { 
    modalTitle?: string;
    modalSubtitle?: string;
    modalProptBy?: string;
    tableName?: string;
    tableLineQuery?: string;
    modalAction?: string;
    modalHasViewing?: boolean;
    // queryData?: IQuery,  
    queryData?: IQueryGeneric
    processData?: (result: any) => JSX.Element
}

export interface QueryListProperties {
    listTitle: string;
    recordTitle: string;
    tableName: string;
    editTableName: string;
    modal?: IQueryListModal;
    editHasCompoundKey: boolean;
    hasIntegrationStatusLegend?: boolean;
    integrationstatusLegend?:{
        status: string,
        color: string,
        description: string
    }[];
    errorCategoryName?: string
    join: IJoin[];
    select: ISelect[],
    where: IWhere[],
    orderBy: IOrderBy[],
    groupBy: string[], //IGroupBy[]
    showFailedTransactionsTable?: boolean, 
} 

export interface IQueryColumnDetails {
    
    aggregate: string | null,
    joinName: string | null,
    columnName: string,
    fullColumnName: string,
    displayColumnName: string,
    fullDisplayColumnName: string,
    tableName: string,
    isControl: boolean,
    isPk: boolean,
    isRequired: boolean | null,
    isMainTable: boolean,
    isStoredInExtra: boolean,
    columnSeq: number | null,
    tableSeq: number | null,
    datatype: string
}

export interface IColumnDetails {
    name: string;
    datatype: string;
    displayName: string;
    seq: number;
    required: boolean;
    pk: boolean;
}

export interface ISchema {
    name: string;
    idColumn: string;
    extraColumn: string;
    createDateColumn: string;
    createUserColumn: string;
    updateDateColumn: string;
    updateUserColumn: string;
    touchDateColumn: string;
    touchUserColumn: string;
    logStatusColumn: string;
    logNumberColumn: string;
    dataColumns: IColumnDetails[],
    extraColumns: IColumnDetails[],
    controlColumns: IColumnDetails[]
}

export enum CastType {
    BIGINT = "BIGINT", 
    BOOLEAN = "BOOLEAN", 
    DATE = "DATE",
    DECIMAL = "DECIMAL",
    FLOAT = "FLOAT", 
    DOUBLE = "DOUBLE",
    INTEGER = "INTEGER",
    JSONB = "JSONB", 
    NUMERIC = "NUMERIC", 
    STRING = "STRING",
    TIME = "TIME", 
    TIMESTAMP = "TIMESTAMP", 
    TIMESTAMPTZ = "TIMESTAMPTZ",    
    UUID = "UUID",     
    VARCHAR = "VARCHAR"
}

export interface IJoin {
    relationship: string; 
    joinType: string;
}

export interface ISelect {
    fullColumnName: string; 
    aggregate: string | null;
}

// export interface IGroupBy {
//     fullColumnName: string;
// }

export interface IWhere {
    fullColumnName: string 
    value: any;
    operator: string; 
    cast?: CastType | null;
    not?: boolean;
}

export interface IOrderBy {
    fullColumnName: string; 
    direction: string; 
    cast?: CastType | null;
}

export interface IFrontEndOnly {
    listTitle: string;
    recordTitle: string;
    editTableName: string;
    editHasCompoundKey: boolean;
}

export interface IIdSelect {
    sql?: string;
    params?: any[];
}

export interface IQuery {
    tableName: string;
    select: ISelect[];
    groupBy: string[]; //IGroupBy[]
    join: IJoin[]; // Defaults to {relationship: 'some_table_name', joinType: 'LEFT'}
    orderBy: IOrderBy[]; // Defaults to {column: 'some_column_name', direction: 'ASC', cast: 'STRING'} 
    where: IWhere[]; // Default to {fullColumnName: string, value: {}, operator: 'EQ', cast: 'STRING'}
    mainTableOnly: boolean;
    includeExtraColumns: boolean;
    includeControlColumns: boolean;
    modifiedAfter: string | null;     // where greatest(rootTable.create_date, rootTable.update_date) > modifiedAfter
    touchedBefore: string | null;      // where rootTable.touch_date < touchedBefore
    isDirty: boolean | null;
    pageNum?: number;
    pageSize?: number;
    frontEndOnly: IFrontEndOnly | null; // Component properties only, server never works with this. 
    idSelect?: IIdSelect | null
}

//New Formates Of Quering Data
export interface IQuerySelectData {
  appCode: string,
  selectCode: string,
  where?: string;
  orderBy?: string;
  pageNum?: number;
  pageSize?: number;
  numRecords?: number;
  params?: Record<string, any>;
}

export interface IQueryTableData {
  tableName: string;
  where?: string;
  orderBy?: string;
  pageNum?: number;
  pageSize?: number;
  numRecords?: number;
  params?:  Record<string, any>;
}

export interface IQueryTableListComms {
    query: IQueryGeneric; 
}

/* Testing */
export interface IQueryGeneric {
  appCode: string | null;
  selectCode: string | null;
  tableName: string | null;
  where: string | null;
  orderBy: string | null;
  pageNum: number | null;
  pageSize: number | null;
  params:  Record<string, any> | null;
}


export interface ColumnSort{
  column: string;
  direction: "asc" | "desc" | null;
};

export interface QueryTableListProperties {
    listTitle: string;
    recordTitle: string;
    tableName: string;
    editTableName: string;
    modal?: IQueryListModal;
    editHasCompoundKey: boolean;
    hasIntegrationStatusLegend?: boolean;
    integrationstatusLegend?:{
        status: string,
        color: string,
        description: string
    }[];
    errorCategoryName?: string
    showFailedTransactionsTable?: boolean, 
    query: IQueryGeneric;
    orderColumns: { name: string; type: string, sortable: boolean, searchable: boolean }[] | null, 
    reportOptions?: { 
        reportQuery?: IQueryGeneric, 
        templateName: string, 
        reportName: string, 
        reportData: Record<string, any> | null } | null,
    failedTransactionsSection?: {
        toggleHeader: React.ReactNode;
        bodyContents: React.ReactNode;
    };
    renderRowData?: (row: any) => React.ReactNode;
    renderActions?: (row: any) => React.ReactNode;
    headerControls?: React.ReactNode;
} 

export enum IntegrationStatus {
    SENT = '#d4a53f', // Orange
    SUCCESS = '#d4a53f', // Green
    CLOSED = '#3ec76b', // Green
    TO_CLOSE = '#d4a53f', // Orange
    PROCESSED = '#3ec76b', // Green
    TO_SEND = '#d4a53f', // Orange
    PICKING = '#d4a53f', // Orange
    DISPATCHED = '#d4a53f', // Orange
    ARRIVED = '#d4a53f', // Orange
    TO_PROCESS = '#d4a53f', // Orange
    FETCHED = '#d4a53f', // Orange
    TO_FETCH = '#d4a53f', // Orange
    RECEIVED = '#3ec76b', // Green
    ERROR = '#bf3636', // Red
    CANCELLED = '#8b8b8c', //Gray
    COMPLETE = '#3ec76b' //Green
}


export interface INotificationSummary {
    category: string;
    description: string;
    last_encountered: string;
    total: number
}

export interface IUploadFilePreviewItem {
  itemCode: string;
  barcode: string;
  name: string;
  count: number;
}

export interface DamagedReturnRow {
  doc_num: string;
  category: string;
  from_whs: string;
  comments: string | null;
}

export interface IQueryExtract {
    sheetName: string;
    queryCreate: IQuery; 
}


export interface IItemBase {
  id: string,
  created: string | null, 
  updated: string | null,
  dirty: boolean,
  touched: string, 
  name: string,
  description: string | null,
  group_name: string | null, 
  opts: string[], 
  product_base_assets: { id: string, url: string }[] | null, 
  product_variants: { id: string, is_closed: boolean, sku: string, name: string}[] | null, 
  is_closed: boolean | null, 
  channels: { id: string, is_enabled: boolean, code: string, num_variants: number}[]| null,
  num_variants: number, // count of 236
  slug: string, 
}

export interface IItemBaseVariant {
  id: string;
  created: string | null, 
  updated: string | null,
  dirty: boolean,
  touched: string, 
  product_base_id: string, 
  opts: Record<string, string>,
  product_base_assets: { id: string, url: string }[] | null,
  price: number,
  name: string, 
  sku: string, 
  is_closed: boolean | null,
  locations: { id: string, code: string, name: string, in_stock: number} [] 
  channels: { id: string, is_enabled: boolean, code: string }[]| null
}

export interface ITableItemChannel {
    id: string,
    code: string,
    channel_data: string | null,
    product_export_script: string,
    base_url: string,
    description: string | null,
    channel_type: string,
    color_code: string
}

export interface IATSItem {
    directory: boolean, 
    isDirectory: boolean,
    path: string,
    name: string,
    lastModified: string, 
} 

export interface IPlanningToolReportsItem {
    id: string, 
    prefix: string, 
    name: string,
    description: string | null
    value: string | number | boolean | string[] | null,
}

export interface IStore {
    id: string;
    code: string;
    name: string;
    email: string
    customer_code: string;
    costing_code: string;
    sit_location: string;
    shopify_location_id: string;
    created: string;
    updated: string| null;
    address?: string; 
}

export interface ISelectedScannedItem {
   id: string;
   barcode: string;
   item_code: string;
   item_group: string;
   name: string;
   product_code: string;
}

/*
 * 
 * ========================
 * Store Return Interfaces
 * ========================
 * 
 * IStoreReturn:
 * Represents a store return transaction between warehouses.
 * Includes high-level document details, status, and quantities.
 * 
 * IStoreReturnLine:
 * Represents an individual item line in a store return document.
 * Includes product info, quantities, and tracking details.
 * 
 */

export interface IStoreReturn {
    id: string;
    created: string; 
    created_by: string;
    updated: string | null; 
    approved_by: string | null;
    doc_num: string;
    from_whs: string;
    to_whs: string;
    notes: string | null;
    status: string | null; 
    damaged: boolean;
    function_call_id: string | null;
    handled_status: string | null; 
    qty_approved: number;
    qty_requested: number;
    total_qty_approved: number | null;
    total_qty_requested: number | null;
    transfer_request_doc_num: string | null;
    completed_at?: string | null;
    extended_message?: string | null;

}

export interface IStoreReturnLine {
    id: string;
    created: string;
    updated: string | null;
    line_num: number;
    item_name: string;
    item_code: string;
    item_barcode: string;
    doc_num: string;
    qty_on_hand: number | null;
    qty_approved: number | null;
    qty_requested: number | null;
}

/*
 *
 * ========================
 * Stock Take Interfaces
 * ========================
 * 
 * IStockTake:
 *   Represents a stock take session.
 *   Includes location, reference, status, timestamps, and session details.
 * 
 * IStockTakeLine:
 *   Represents an individual counted item line within a stock take.
 *   Contains product info, quantity counted, and session linkage.
 * 
 * IStockReportReview:
 *   Represents a comparison between expected and actual stock during review.
 *   Includes variance calculations, on-hand quantities, and item details.
 * 
 */

export interface IStockTake {
    id: string;
    location: string;
    comment: string | null;
    seq: number;
    reference: string;
    created_by: string;
    created: string;
    updated: string;
    initialised: string | null;
    finalised: string | null;
    last_session_num: number;
    deleted_by: string | null;
    file_uploaded: boolean | null;
    is_cycle_count: boolean;
    status: string;
}

export interface IStockTakeLine {
    id: string;
    stocktake_id: number;
    created_by: string;
    created: string;
    updated: string;
    session_num: number | null;
    item_code: string; 
    item_name: string;
    item_barcode: string;
    qty: number| null;
}

export interface IStockReportReview {
    actual_variance : number;
    available_start : number;
    committed_final : number;
    committed_start : number;
    item_code: string;
    location: string;
    name: string;
    on_hand_final: number;
    on_hand_start: number;
    reference: string;
    scanned_qty: number;
    scanned_variance: number;
    stocktake_id : string;
}

export interface IFailedTransaction {
    id: string;
    key: string; 
    function_call_id: string;
    completed_at: string;
    extended_message: string; 
    handled_status: string;
}

export interface IUploadResourceResult {
  fileName: string;
  success: boolean;
  status: number;
  error?: any;
}


//Testing new section


export interface IQueryTableColumnDetails {
    
    // aggregate: string | null,
    // joinName: string | null,
    // columnName: string,
    // fullColumnName: string,
    // displayColumnName: string,
    // fullDisplayColumnName: string,
    // tableName: string,
    // isControl: boolean,
    // isPk: boolean,
    // isRequired: boolean | null,
    // isMainTable: boolean,
    // isStoredInExtra: boolean,
    // columnSeq: number | null,
    // tableSeq: number | null,
    // datatype: string

     name: string,
     dataType: string,
     precision: number,
     scale: number,
     isId: boolean,
     isKey:  boolean,
     isControl:  boolean,
     isData:  boolean,
     isNullable:  boolean | null,
     hasDefault:  boolean | null,
}
